import { HttpParams } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { Tierreklamation } from '../../../../projects/kiene-core/src/lib/components/gruppe-tier/classes/tierreklamation';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneTableService } from '../../components/kiene-table/kiene-table.service';
import { GruppeTier } from '../../../../projects/kiene-core/src/lib/components/gruppe-tier/classes/gruppe-tier';
import { MatDialog } from '@angular/material/dialog';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { StammdatenService } from '../../../../projects/kiene-core/src/lib/components/stammdaten/stammdaten.service';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { EnterValueDialogComponent } from '../../../../projects/kiene-core/src/lib/components/dialogs/enter-value-dialog/enter-value-dialog.component';
import {
    EnterValueInput,
    EnterValueResponse,
} from '../../../../projects/kiene-core/src/lib/components/dialogs/enter-value-dialog/enter-value-response';

@Component({
    selector: 'kiene-add-gruppe-tier-reklamation-dialog',
    templateUrl: './add-gruppe-tier-reklamation-dialog.component.html',
    styleUrls: ['./add-gruppe-tier-reklamation-dialog.component.scss'],
})
export class AddGruppeTierReklamationDialogComponent implements OnInit, OnDestroy {
    tierreklamationen: Tierreklamation[] = [];
    selectedIds = new Set<number>();
    gruppeTier: GruppeTier;
    canCreateTierreklamation = false;
    isCreating = false;
    private pendingCreatedReklamationText: string | null = null;
    private loadReklamationenSub: Subscription;
    private loadGruppeTierSub: Subscription;

    constructor(
        public dialogRef: MatDialogRef<AddGruppeTierReklamationDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: { gruppeTier?: GruppeTier; gt_id?: number },
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneTableService,
        private dialog: MatDialog,
        private localStorageService: LocalStorageService,
        private stammdatenService: StammdatenService,
        private messageService: MessageService,
    ) {
        this.gruppeTier = this.data?.gruppeTier ?? ({ gt_id: this.data?.gt_id } as GruppeTier);
    }

    ngOnInit(): void {
        this.canCreateTierreklamation = this.currentUserCanCreateTierreklamation();
        this.loadReklamationen();
        this.loadGruppeTier();
    }

    ngOnDestroy(): void {
        this.loadReklamationenSub?.unsubscribe();
        this.loadGruppeTierSub?.unsubscribe();
    }

    addRemoveReklamation(event: MatCheckboxChange, tierreklamation: Tierreklamation): void {
        const id = Number(tierreklamation?.tierreklamation_id);
        if (!id) {
            return;
        }
        if (event.checked) {
            this.selectedIds.add(id);
        } else {
            this.selectedIds.delete(id);
        }
    }

    isChecked(tierreklamation: Tierreklamation): boolean {
        const id = Number(tierreklamation?.tierreklamation_id);
        return id > 0 && this.selectedIds.has(id);
    }

    loadReklamationen(): void {
        this.loadReklamationenSub?.unsubscribe();
        const params = new HttpParams().set('freigegeben', '1');
        this.loadReklamationenSub = this.api
            .get(this.apiBaseUrl + 'tierreklamation/read_kundenportal.php', params)
            .subscribe({
                next: response => {
                    this.tierreklamationen = response.records;
                    if (this.pendingCreatedReklamationText) {
                        const created = this.tierreklamationen.find(
                            t => (t?.reklamation ?? '').trim() === this.pendingCreatedReklamationText,
                        );
                        if (created?.tierreklamation_id) {
                            this.selectedIds.add(Number(created.tierreklamation_id));
                        }
                        this.pendingCreatedReklamationText = null;
                    }
                },
            });
    }

    openCreateTierreklamationDialog(): void {
        if (!this.canCreateTierreklamation || this.isCreating) {
            return;
        }

        const input = new EnterValueInput();
        input.title = 'Tierreklamation anlegen';
        input.addInputValue('Bezeichnung', 'text');

        this.dialog
            .open(EnterValueDialogComponent, { data: input })
            .afterClosed()
            .subscribe({
                next: (result: EnterValueResponse) => {
                    const text = result?.getValue(0)?.trim?.();
                    if (!text) {
                        return;
                    }
                    this.isCreating = true;
                    this.pendingCreatedReklamationText = text;
                    this.stammdatenService.addTierreklamation(text).subscribe({
                        next: () => {
                            this.isCreating = false;
                            this.loadReklamationen();
                        },
                        error: err => {
                            this.isCreating = false;
                            this.pendingCreatedReklamationText = null;
                            this.messageService.errorMessage('Fehler beim Anlegen der Tierreklamation', err);
                        },
                    });
                },
            });
    }

    close(): void {
        this.dialogRef.close();
    }

    save(): void {
        this.gruppeTier.reklamationen = this.tierreklamationen.filter(t =>
            this.selectedIds.has(Number(t.tierreklamation_id)),
        );
        this.dialogRef.close(this.gruppeTier);
    }

    private loadGruppeTier(): void {
        const gtId = Number(this.gruppeTier?.gt_id || this.data?.gt_id || 0);
        if (gtId <= 0) {
            return;
        }

        this.loadGruppeTierSub?.unsubscribe();
        this.loadGruppeTierSub = this.api
            .getOne(this.apiBaseUrl + 'gruppe_tier/read_one_kundenportal.php', 'gt_id', String(gtId))
            .subscribe({
                next: (gt: GruppeTier) => {
                    this.gruppeTier = gt;
                    this.applyExistingReklamationen(gt?.reklamationen);
                },
                error: err => {
                    this.applyExistingReklamationen(this.gruppeTier?.reklamationen);
                    this.messageService.errorMessage('Tier konnte nicht geladen werden!', err);
                },
            });
    }

    private applyExistingReklamationen(reklamationen: Tierreklamation[] | null | undefined): void {
        this.selectedIds.clear();
        for (const reklamation of reklamationen ?? []) {
            const id = Number(reklamation?.tierreklamation_id);
            if (id > 0) {
                this.selectedIds.add(id);
            }
        }
    }

    private currentUserCanCreateTierreklamation(): boolean {
        return this.localStorageService.hasPermission(9232);
    }
}
